'use client';

import React from 'react';
import { logger } from '@/lib/logger';

interface ErrorBoundaryProps {
  name: string;
  fallback: React.ReactNode;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/* Catches a render-time crash from one provider, logs it, and renders `fallback` instead so the
   rest of the tree keeps working. */
export class ProviderErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error(`Provider "${this.props.name}" crashed`, error, {
      feature: 'provider-error-boundary',
      route: this.props.name,
      componentStack: info.componentStack ?? undefined,
    });
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

interface IsolatedProviderProps<TProps extends object = Record<string, never>> {
  name: string;
  Provider: React.ComponentType<TProps & { children: React.ReactNode }>;
  providerProps?: TProps;
  children: React.ReactNode;
}

/* Drop-in wrapper that runs a provider behind its own error boundary. Adding a new isolated
   provider is one JSX line — pass `Provider` (the component) and optionally `providerProps`. */
export function IsolatedProvider<TProps extends object = Record<string, never>>({
  name,
  Provider,
  providerProps,
  children,
}: IsolatedProviderProps<TProps>) {
  return (
    <ProviderErrorBoundary name={name} fallback={<>{children}</>}>
      <Provider {...((providerProps ?? {}) as TProps)}>{children}</Provider>
    </ProviderErrorBoundary>
  );
}
